import React, { useEffect, useState } from 'react';
import InvoiceLink from './InvoiceLink';
import { Ticket, MapPin, Wallet, Smartphone } from 'lucide-react';
import apiService from '../apiService';
import mmPay from './mmPay';
import { CinemaTicketsBlock } from './CinemaPages';

/**
 * Billetterie web — liste des événements, détail (catégories de billets,
 * quantité, achat solde ou Mobile Money pay-first + confirm), mes billets.
 * Mêmes endpoints que le natif : /events, /events/:id, /events/:id/order,
 * /events/orders/:id/confirm, /events/my/tickets.
 */
const fmtDate = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function EventsList({ nav, citySlug }) {
  const [events, setEvents] = useState(null);
  const [featured, setFeatured] = useState([]);   // « À l'affiche » : sélection admin (≤ 5) complétée (2026-11-04)
  useEffect(() => {
    apiService.get('/events').then(r => setEvents(r?.events || [])).catch(() => setEvents([]));
    if (citySlug) apiService.get(`/events/featured?city=${encodeURIComponent(citySlug)}`).then(r => setFeatured(r?.events || [])).catch(() => {});
  }, [citySlug]);

  if (events === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;

  const mine = citySlug ? events.filter(e => e.city_slug === citySlug) : events;
  const others = citySlug ? events.filter(e => e.city_slug !== citySlug) : [];
  const Block = ({ title, list }) => !list.length ? null : (
    <section className="mk-section">
      <div className="mk-section-head"><h2>{title}</h2></div>
      <div className="mk-row">
        {list.map(e => (
          <button key={e.event_id} className="mk-card" onClick={() => nav({ page: 'event', eventId: e.event_id })}>
            <div className="mk-card-imgwrap">
              {e.poster ? <img className="mk-card-img" src={e.poster} alt="" loading="lazy" /> : <div className="mk-card-img mk-card-ph">🎟️</div>}
              <span className="mk-eta">{fmtDate(e.starts_at)}</span>
              {e.status === 'soldout' && <span className="mk-flag">Complet</span>}
            </div>
            <span className="mk-card-title">{e.title}</span>
            {e.venue?.name && <span className="mk-meta"><MapPin size={11} /> {e.venue.name}</span>}
          </button>
        ))}
      </div>
    </section>
  );

  return (
    <div className="mk-container">
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="mk-btn ghost" onClick={() => nav({ page: 'cinema' })}>🎬 Cinéma</button>
        <button className="mk-btn ghost" onClick={() => nav({ page: 'tickets' })}><Ticket size={16} /> Mes billets</button>
      </div>
      {featured.length > 0 && (
        <section className="mk-section">
          <div className="mk-section-head"><h2>⭐ À l’affiche</h2></div>
          <div className="mk-row">
            {featured.map(e => (
              <button key={`f_${e.event_id}`} className="mk-card" style={{ minWidth: 260 }} onClick={() => nav({ page: 'event', eventId: e.event_id })}>
                <div className="mk-card-imgwrap">
                  {e.poster ? <img className="mk-card-img" src={e.poster} alt="" loading="lazy" style={{ height: 170 }} /> : <div className="mk-card-img mk-card-ph" style={{ height: 170 }}>🎟️</div>}
                  <span className="mk-eta">{fmtDate(e.starts_at)}</span>
                  {e.featured && <span className="mk-flag">À l’affiche</span>}
                </div>
                <span className="mk-card-title">{e.title}</span>
                {e.venue?.name && <span className="mk-meta"><MapPin size={11} /> {e.venue.name}</span>}
              </button>
            ))}
          </div>
        </section>
      )}
      <Block title={citySlug ? `À ${citySlug.charAt(0).toUpperCase() + citySlug.slice(1)}` : 'Événements'} list={featured.length ? mine.filter(e => !featured.some(f => f.event_id === e.event_id)) : mine} />
      <Block title="Dans les autres villes" list={others} />
      {!events.length && <div className="mk-center"><Ticket size={42} /> Aucun événement à venir.</div>}
    </div>
  );
}

export function EventDetail({ eventId, nav, currentUser }) {
  const [data, setData] = useState(null);
  const [tier, setTier] = useState(null);
  const [qty, setQty] = useState(1);
  const [pay, setPay] = useState('balance');
  const [phone, setPhone] = useState('');
  const [telecom, setTelecom] = useState('AM');
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiService.get(`/events/${eventId}`).then(r => {
      if (r?.success) { setData(r); setTier((r.tiers || []).find(t => t.available > 0) || null); }
    }).catch(() => {});
  }, [eventId]);

  if (!data) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  const { event, tiers } = data;
  const total = tier ? tier.price * qty : 0;
  const maxQ = Math.min(event.max_tickets_per_order || 10, tier?.available || 1);

  const buy = async () => {
    setError(null); setBusy(true);
    try {
      if (!tier) throw new Error('Choisissez une catégorie de billets.');
      const res = await apiService.post(`/events/${eventId}/order`, { tier_id: tier.tier_id, quantity: qty, payment_method: pay });
      if (!res.success) throw new Error(res.error || 'Achat impossible');
      if (pay === 'balance') { nav({ page: 'tickets' }); return; }
      // Mobile Money : pay-first puis confirmation de la commande réservée.
      const { paymentId } = await mmPay({
        userId: currentUser.user_id, amount: res.amount_total, phone, telecom,
        label: `Billets ${event.title}`, planType: 'event_ticket', onPhase: setPhase,
      });
      setPhase('Émission de vos billets…');
      const conf = await apiService.post(`/events/orders/${res.order_id}/confirm`, { payment_ref: paymentId });
      if (!conf.success) throw new Error(conf.error || 'Confirmation impossible');
      nav({ page: 'tickets' });
    } catch (e) { setError(e?.error || e?.message || 'Erreur'); }
    finally { setBusy(false); setPhase(null); }
  };

  return (
    <div className="mk-container">
      <div className="mk-two">
        <div className="mk-panel" style={{ padding: 0, overflow: 'hidden' }}>
          {event.poster ? <img src={event.poster} alt="" style={{ width: '100%', maxHeight: 360, objectFit: 'cover', display: 'block' }} /> : null}
          <div style={{ padding: 18 }}>
            <h3 style={{ fontSize: '1.3rem' }}>{event.title}</h3>
            <p className="mk-note">📅 {fmtDate(event.starts_at)}{event.venue?.name ? ` · 📍 ${event.venue.name}` : ''}</p>
            {event.description && <p className="mk-desc" style={{ marginTop: 10 }}>{event.description}</p>}
          </div>
        </div>

        <div className="mk-panel">
          <h3>Billets</h3>
          {(tiers || []).map(t => (
            <button key={t.tier_id} className={`mk-opt${tier?.tier_id === t.tier_id ? ' on' : ''}`} disabled={t.available <= 0}
              onClick={() => { setTier(t); setQty(1); }}>
              <span className="box">{tier?.tier_id === t.tier_id ? '✓' : ''}</span>
              <span className="name">{t.name} {t.available <= 0 ? '— épuisé' : `(${t.available} dispo)`}</span>
              <span className="delta">{t.price.toLocaleString('fr-FR')} {t.currency || 'FC'}</span>
            </button>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0' }}>
            <span style={{ fontWeight: 700 }}>Quantité</span>
            <div className="mk-qty">
              <button onClick={() => setQty(v => Math.max(1, v - 1))}>−</button><b>{qty}</b>
              <button onClick={() => setQty(v => Math.min(maxQ, v + 1))}>+</button>
            </div>
          </div>
          <div className="mk-pay">
            <button className={pay === 'balance' ? 'on' : ''} onClick={() => setPay('balance')}><Wallet size={19} />Solde Mbiyo</button>
            <button className={pay === 'mobile_money' ? 'on' : ''} onClick={() => setPay('mobile_money')}><Smartphone size={19} />Mobile Money</button>
          </div>
          {pay === 'mobile_money' && (
            <div style={{ marginTop: 10 }}>
              <div className="mk-field"><label>Numéro Mobile Money</label>
                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="0991234567" inputMode="tel" /></div>
              <div className="mk-field"><label>Opérateur</label>
                <select value={telecom} onChange={e => setTelecom(e.target.value)}>
                  <option value="AM">Airtel Money</option><option value="MP">M-Pesa</option><option value="OM">Orange Money</option>
                </select></div>
            </div>
          )}
          <div className="mk-total grand"><span>Total</span><span>{total.toLocaleString('fr-FR')} FC</span></div>
          {error && <p className="mk-note err" style={{ marginTop: 8 }}>{error}</p>}
          <button className="mk-btn primary block" style={{ marginTop: 12 }} disabled={busy || !tier} onClick={buy}>
            {busy ? (phase || 'Achat en cours…') : `Acheter — ${total.toLocaleString('fr-FR')} FC`}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MyTickets({ nav }) {
  const [tickets, setTickets] = useState(null);
  useEffect(() => {
    apiService.get('/events/my/tickets').then(r => setTickets(r?.tickets || [])).catch(() => setTickets([]));
  }, []);
  if (tickets === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  return (
    <div className="mk-container">
      <CinemaTicketsBlock />
      {!tickets.length ? <div className="mk-center" style={{ marginTop: 16 }}><Ticket size={42} /> Aucun billet d'événement. <button className="mk-btn primary" onClick={() => nav({ page: 'events' })}>Voir les événements</button></div> : (
      <div className="mk-panel" style={{ marginTop: 16 }}>
        <h3>🎟️ Mes billets</h3>
        {tickets.map(t => (
          <div className="mk-line" key={t.ticket_id}>
            <div className="info">
              <div className="name">{t.event?.title || 'Événement'}</div>
              <div className="opt">{t.event ? fmtDate(t.event.starts_at) : ''} · {t.tier_name || ''}</div>
              {t.order_id && <InvoiceLink type="ticket" id={t.order_id} compact />}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 900, letterSpacing: 3, color: t.status === 'used' ? 'var(--mb-text-3)' : 'var(--mb-fire)', fontVariantNumeric: 'tabular-nums' }}>
                {t.ticket_code || t.code}
              </div>
              <span className={`mk-open ${t.status === 'valid' ? 'on' : 'off'}`}>{t.status === 'valid' ? 'Valide' : 'Utilisé'}</span>
            </div>
          </div>
        ))}
        <p className="mk-note" style={{ marginTop: 10 }}>Présentez le code à l'entrée — le contrôleur le vérifie dans son application.</p>
      </div>
      )}
    </div>
  );
}
